import { inject, provide, reactive, type InjectionKey } from 'vue'
import dayjs from 'dayjs'
import { addDays, profileOffset, todayKey, toLocal } from './exchangeFormat'
import type { ArchiveResult, ExchangeType, NamedRef, PageSize, RecordType, RequestDirection, RequestLifecycle, VacancySegment } from './exchangeTypes'

// Состояние раздела биржи (RD-164): живёт, пока открыт раздел. Черновики форм и фильтры
// каждой вкладки сохраняются при переключении вкладок и не сохраняются после ухода из раздела.

export interface BatchPending {
    key: string
    payload: Record<string, unknown>
    dates: string[]
}

export interface VacancyDraft {
    date: string | null
    start: string | null
    end: string | null
    positionId: string
    exchangeType: ExchangeType
    comment: string
    commentOpen: boolean
    copyOn: boolean
    copyDates: string[]
    // Ошибки дат последнего пакета: дата → сообщение сервера.
    dateErrors: Record<string, string>
    // Пакет с неизвестным исходом: повтор только с тем же ключом и payload.
    pending: BatchPending | null
}

export interface RequestDraft {
    date: string | null
    start: string | null
    end: string | null
    fromDepartment: NamedRef | null
    employeeId: string
    positionId: string
    comment: string
    commentOpen: boolean
}

interface ListFilters {
    period: [string, string]
    positionIds: string[]
    search: string
    page: number
    pageSize: PageSize
}

export interface VacanciesFilters extends ListFilters {
    segment: VacancySegment
    isUrgent: boolean | null
    exchangeType: ExchangeType | null
}

export interface RequestsFilters extends ListFilters {
    direction: RequestDirection
    lifecycle: RequestLifecycle[]
}

export interface ArchiveFilters extends ListFilters {
    recordType: RecordType | null
    exchangeType: ExchangeType | null
    result: ArchiveResult[]
}

export interface ExchangePage {
    vacancyDraft: VacancyDraft
    requestDraft: RequestDraft
    vacancies: VacanciesFilters
    requests: RequestsFilters
    archive: ArchiveFilters
    // ID успешно созданных за время в разделе — плашка «Новая».
    createdIds: string[]
    // Раскрытые строки по ID; исчезнувшие записи удаляются при обновлении списка.
    expanded: string[]
    // Вакансии с выполняющимся действием: конфликтующие кнопки недоступны.
    busy: string[]
}

const KEY: InjectionKey<ExchangePage> = Symbol('exchange-page')

/** RD-79: сегодня — начало через 15 минут и час работы (не позже 23:59); иначе 09:00–18:00. */
export function defaultTimes(date: string | null): { start: string, end: string } {
    const offset = profileOffset()
    if (!date || date !== todayKey(offset)) return { start: '09:00', end: '18:00' }
    const now = toLocal(dayjs().toISOString(), offset)
    const lastMinute = now.hour(23).minute(59)
    const start = now.add(15, 'minute').isAfter(lastMinute) ? lastMinute : now.add(15, 'minute')
    const end = start.add(1, 'hour').isAfter(lastMinute) ? lastMinute : start.add(1, 'hour')
    return { start: start.format('HH:mm'), end: end.format('HH:mm') }
}

export function emptyVacancyDraft(): VacancyDraft {
    const date = todayKey()
    return {
        date,
        ...defaultTimes(date),
        positionId: '',
        exchangeType: 'internal',
        comment: '',
        commentOpen: false,
        copyOn: false,
        copyDates: [],
        dateErrors: {},
        pending: null,
    }
}

export function emptyRequestDraft(): RequestDraft {
    return {
        date: todayKey(),
        start: '09:00',
        end: '18:00',
        fromDepartment: null,
        employeeId: '',
        positionId: '',
        comment: '',
        commentOpen: false,
    }
}

/** Мои вакансии и Запросы: сегодня + 6 дней. Архив: 7 дней по сегодня. */
export function defaultPeriod(view: 'vacancies' | 'requests' | 'archive'): [string, string] {
    const today = todayKey()
    return view === 'archive' ? [addDays(today, -6), today] : [today, addDays(today, 6)]
}

export function createExchangePage(): ExchangePage {
    const page = reactive<ExchangePage>({
        vacancyDraft: emptyVacancyDraft(),
        requestDraft: emptyRequestDraft(),
        vacancies: { segment: 'active', period: defaultPeriod('vacancies'), positionIds: [], isUrgent: null, exchangeType: null, search: '', page: 1, pageSize: 15 },
        requests: { direction: 'incoming', period: defaultPeriod('requests'), lifecycle: [], positionIds: [], search: '', page: 1, pageSize: 15 },
        archive: { period: defaultPeriod('archive'), positionIds: [], recordType: null, exchangeType: null, result: [], search: '', page: 1, pageSize: 15 },
        createdIds: [],
        expanded: [],
        busy: [],
    })
    provide(KEY, page)
    return page
}

export function useExchangePage(): ExchangePage {
    const page = inject(KEY)
    if (!page) throw new Error('useExchangePage: страница биржи не создана')
    return page
}

export function toggleIn(list: string[], id: string, on: boolean): string[] {
    const has = list.includes(id)
    if (on && !has) return [...list, id]
    if (!on && has) return list.filter(item => item !== id)
    return list
}
